import styled from '@emotion/styled'
import {theme} from '../../../theme'
import {palette} from '../../../theme/colors'
import {Button} from '../../../shared/Button'

export const Layout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.space[5]};
  width: 100%;
`

export const MainColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  padding: ${theme.space[2.5]};
`

// Wraps PageHeading + StepCardWrapper together (see PaymentDetailsStep.js)
// so StatusOverlay below can cover the whole step — heading included, not
// just the card — while the Payment Status drawer is open.
export const StepRootWrapper = styled.div`
  position: relative;
  width: 100%;
`

// Shown over StepRootWrapper while the Payment Status drawer is open — a
// translucent white sheet + a light backdrop blur, instead of blurring the
// step's own text/inputs directly (which made them unreadable rather than
// just visually de-emphasized). pointer-events: none lets StepCardWrapper's
// own pointer-events: none (already disabling the card underneath) be the
// single source of truth — this overlay never needs to catch clicks itself.
export const StatusOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(1px);
  border-radius: inherit;
  pointer-events: none;
  z-index: ${theme.zIndex.overlay};
`

// The Payment Status drawer, docked to the right edge of the BROWSER
// viewport (position: fixed, not relative to Layout/StepCard's narrower,
// padded bounds) so it reads as a page-level drawer rather than something
// confined to the step's own card. right: 0 is flush against the true
// viewport edge; top/bottom: 0 span the full browser height.
// IMPORTANT: PaymentDetailsStep.js renders this via createPortal into
// document.body, not in place. The standalone host's <main> establishes its
// own stacking context (a CSS Grid item with z-index: 9, in
// src/components/ui/app.js), which traps any z-index set on elements
// nested inside it — no z-index on this dock could ever out-rank the app
// header (z-index: 98) while still living inside that trapped context.
// Portaling to document.body escapes it so this dock's z-index actually
// competes against the header's, letting it render above the header at
// full height instead of going behind it.
// CollapseTab (36px) + StatusPanel (380px, flush against it — no gap) are
// rendered in that DOM order (tab first) inside a box whose width is fixed
// at their combined total, always — only `transform: translateX` animates.
// Open (`open` true) rests at translateX(0), its natural position: tab
// sits immediately left of StatusPanel, which is flush against the true
// viewport edge. Closed shifts the WHOLE box right by StatusPanel's own
// width — precisely enough to push StatusPanel entirely past the viewport
// edge (hidden) while sliding the tab into the space StatusPanel vacated,
// landing it flush at the viewport edge instead. Because both are one
// rigid box, they always move TOGETHER — the tab visibly travels along
// with the drawer as it opens/closes, ending each transition at a fixed
// "resting" spot (open: beside the panel; closed: at the true edge) rather
// than never moving at all. NOTE: StatusPanel's width (380px) is
// duplicated here — keep both in sync if it ever changes.
export const PaymentStatusDock = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: calc(36px + 380px);
  z-index: ${theme.zIndex.sticky};
  display: flex;
  transform: translateX(${({open}) => (open ? '0' : '380px')});
  transition: transform 0.4s ease-in-out;
`

// Full width so it lines up with StepCardWrapper below it (both
// SchedulingStep and PaymentDetailsStep default StepCardWrapper to 100%),
// instead of shrinking to its own content width under ContentArea's
// align-items: center. margin-bottom matches AccountInfo's PageHeader so the
// gap above every step's card is consistent regardless of which header
// component is in play.
export const PageHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  width: 100%;
  margin-bottom: ${theme.space[4]};
  padding: 0 6px;
`

export const HeadingTitle = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
`

export const HeadingSubtitle = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  background: ${theme.colors.bg.primary};
  border: 1px solid ${theme.colors.border.default};
  border-radius: ${theme.radii.lg2};
  padding: ${theme.space[5]};
  box-shadow: ${theme.shadows.sm};
  gap: ${theme.space[2]};
`

export const PanelTitle = styled.h3`
  margin: 0 0 ${theme.space[3]};
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
`

// The Bill To row's own container. StepCard renders with noPadding for this
// step (see PaymentDetailsStep.js), so this row is flush against the
// card's actual edges at full width with no margin/calc bleed trick needed
// — TwoColumnRow below carries its own padding to recreate the inset
// StepCard used to provide for the rest of the content. Top corners match
// StepCard's own radius since there's no padding buffer between them. A
// border (not a shadow) separates it from the content below.
export const BillToPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[2]};
  width: 100%;
  background: ${theme.colors.bg.primary};
  // border-radius: ${theme.radii.xl} ${theme.radii.xl} 0 0;
  // border-bottom: 1px solid ${theme.colors.border.default};
  // padding: ${theme.space[2]} ${theme.space[2.5]};
  padding: 0px 10px 6px;
`

// Two sides: BillToSummary's details on the left, the Send Via toggle +
// Send Payment Link button grouped together on the right (BillToRowActions).
export const BillToRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.space[5]};
`

// Right side of BillToRow — the Send Via toggle and the Send Payment Link
// button/banner sit side by side here, both pushed to the row's right edge.
// align-items: flex-end (not center) so the buttons themselves line up on
// one row — SendViaFieldGroup is taller than SendLinkButtonGroup because of
// the "Send Payment Link Via" label sitting above its toggle, so centering
// the two groups would center that extra height instead of the buttons.
export const BillToRowActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${theme.space[5]};
  flex-shrink: 0;
`

// Send Payment Link button — uses the "solid-dark-blue" variant (see
// PaymentDetailsStep.js), the same variant/size as the account list's
// "Create Purchase Link" button, so both read as the same dark-navy,
// rounded-rectangle button style instead of the lighter pill look this used
// to have.
export const SendPaymentLinkButton = styled(Button)`
  height: 32px;
`

export const SendLinkButtonGroup = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-bottom: 4px;
`

// Two side-by-side BillToSummary groups — Send Invoice to/Bill To on the
// left, Contact/Phone in the next column to the right.
export const BillToDetails = styled.div`
  display: flex;
  gap: ${theme.space[6]};
  flex-wrap: wrap;
`

// Compact label+value summary lines (Send Invoice to / Bill To / Contact /
// Phone) — matches the reference design's plain text header instead of a
// card-style field grid.
export const BillToSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
`

export const BillToSummaryLine = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
`

export const BillToSummaryLabel = styled.span`
  font-weight: ${theme.fontWeights.bold};
  margin-right: ${theme.space[1]};
`

export const BillToSummaryName = styled.span`
  font-weight: ${theme.fontWeights.bold};
`

export const BillToSummaryAddress = styled.span`
  margin-left: ${theme.space[2]};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.fontWeights.regular};
`

// Location cards, mirroring SchedulingStep's LocationTab design (pin icon +
// index label, bold name, meta line below — see SchedulingStep.styles.js's
// LocationTabCard family) so both steps' location lists read as the same
// pattern. Static here (no click-to-select). No longer wrapped in the
// shared Panel (border + one shadow around the whole list) — each
// LocationCard below carries its own box-shadow instead.
export const LocationCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[3]};
  // margin: 20px 0 0 0;
`

export const LocationCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${theme.colors.bg.primary};
  gap: 10px;
  opacity: 1;
  border-radius: 10px;
  // border-top-left-radius: 10px;
  // border-bottom-left-radius: 10px;
  padding-top: 8px;
  padding-right: 8px;
  padding-bottom: 8px;
  padding-left: 8px;
  border-top-width: 1px;
  border-bottom-width: 1px;
  border-left-width: 1px;
  border: none;
  box-shadow: 0px 2px 6.1px 0px #0000002b;
`

export const LocationCardRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.space[2]};
  width: 100%;
`

export const LocationCardMainIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #e1eaff;
  width: 24;
  height: 26;
  gap: 10px;
  angle: -180 deg;
  opacity: 1;
  border-radius: 6px;
  padding-top: 7px;
  padding-right: 6px;
  padding-bottom: 7px;
  padding-left: 6px;
`

export const LocationCardDetailsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  flex: 1;
  min-width: 0;
`

export const LocationCardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space[2]};
`

export const LocationCardIndexLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${theme.space[1]};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  letter-spacing: 0.02em;
  color: #0327a9;

  font-weight: 500;
  font-style: Medium;
  font-size: 10px;
  leading-trim: NONE;
  line-height: 15px;
  letter-spacing: 0.25px;
  vertical-align: middle;
  text-transform: uppercase;
`

// Right side of LocationCardTopRow — how many of this location's items are
// new (not already included in an existing plan), from
// buildLocationOrderSummary's newItemCount.
export const LocationCardNewItems = styled.span`
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  color: #0327a9;
  white-space: nowrap;
`

export const LocationCardName = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const LocationCardPay = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.space[1]};
  font-size: ${theme.fontSizes.xs};
  white-space: nowrap;
`

// "→ Pay - $X" — bold/black.
export const LocationCardPayAmount = styled.span`
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
`

// "+ $Y/mo" — muted, de-emphasized against the amount above.
export const LocationCardPayMonthly = styled.span`
  font-weight: ${theme.fontWeights.regular};
  color: background: #808080;
  font-family: Open Sans;
  font-weight: 400;
  font-style: Regular;
  font-size: 11px;
  leading-trim: NONE;
  line-height: 16.5px;
  letter-spacing: 0%;
  vertical-align: middle;
`

export const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.6fr;
  gap: ${theme.space[5]};
  padding: ${theme.space[2]};
  // background: ${theme.colors.bg.brandAlt};
  border-radius: ${theme.radii.xl};
  background-color: rgb(244, 246, 251);
  border: 1px solid rgb(229, 231, 235);
  // border: 1px solid rgb(206, 220, 249);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const FullWidthCell = styled.div`
  grid-column: 1 / -1;
  width: 700px;
  margin: 0 auto;
  border-radius: ${theme.radii.lg2};
`

// Right column of BillToRow — no margin-top now that it sits beside
// BillToSummary instead of stacked below the old field grid.
export const SendViaFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  flex-shrink: 0;
`

export const FieldLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  // text-transform: uppercase;
  // letter-spacing: 0.04em;
  font-weight: ${theme.fontWeights.bold};
`

// Segmented toggle track (same pattern as SchedulingStep's LanguageToggle/
// LanguageOption Spanish switch) — one bordered/shadowed pill container
// holds both options, instead of each option carrying its own border+shadow,
// so switching reads as one control rather than two separate buttons.
export const SendViaToggle = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 4px;
  gap: 2px;
  // border: 1px solid ${theme.colors.border.default};
  border-radius: ${theme.radii.md};
  background: ${theme.colors.bg.primary};
  box-shadow: 0px 2px 6.1px 0px #0000002b;
`

export const SendViaOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.space[2]};
  height: 100%;
  padding: 0 ${theme.space[5]};
  white-space: nowrap;
  border: none;
  border-radius: ${theme.radii.md};
  background: ${({active}) => (active ? '#26325f' : 'transparent')};
  color: ${({active}) =>
    active ? theme.colors.action.primaryText : theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${({active}) =>
    active ? theme.fontWeights.semibold : theme.fontWeights.regular};
  cursor: pointer;
  transition: background ${theme.transitions.fast},
    color ${theme.transitions.fast};

  svg {
    width: 16px;
    height: 16px;
  }

  svg path {
    stroke: currentColor;
    transition: stroke ${theme.transitions.fast};
  }

  &:hover:not(:disabled) {
    background: ${({active}) =>
      active ? '#1a2347' : theme.colors.bg.secondary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

// ── Payment Status drawer ────────────────────────────────────────────────
// Static/full-size always — PaymentStatusDock (see above) is what animates
// now, sliding this panel (and CollapseTab together with it) off past the
// viewport edge when closed via transform, so this panel itself no longer
// needs to shrink or hide its own chrome.
export const StatusPanel = styled.div`
  width: 380px;
  min-width: 0;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.space[4]};
  padding: 81px 10px;
  // border-radius: ${theme.radii.lg2};
  background: #bdc6e5;
  border-left: 1.5px solid #5378fc;
  box-shadow: -4px 0px 8.2px 0px #00000014 inset;
  overflow-y: auto;
`
export const StatusPanelContent = styled.div`
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    180deg,
    #e1eaff 0%,
    #ffffff 30.68%,
    #fefefe 76.18%,
    #e1eaff 100%
  );
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
`

export const StatusPanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.space[3]};
`

// align-items: flex-start (not center) since this column now holds two
// lines (StatusHeaderTextGroup's label + email) — the icon lines up with
// the top line instead of being centered against the whole two-line block.
export const StatusHeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.space[2]};
  min-width: 0;
`

export const StatusIconBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: ${theme.radii.full};
  background: ${theme.colors.bg.primary};
  color: ${theme.colors.text.brand};
  box-shadow: ${theme.shadows.sm};
`

// Column 1 of the two-column header: "Payment Link Sent To" (bold caps)
// stacked above the recipient's email (plain, muted) — replaces the old
// standalone SentToRow box, which used to render the email separately
// below the header instead of inside it.
export const StatusHeaderTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const StatusHeaderLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.colors.text.primary};
`

export const StatusHeaderEmail = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// Column 2 of the two-column header: "Expires in" stacked above the
// countdown value, right-aligned.
export const ExpiresPill = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 0 0 auto;
`

export const ExpiresLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
`

export const ExpiresValue = styled.span`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
`

export const StatusDivider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${theme.colors.border.alt};
  margin: 0;
`

// Wraps the "Payment Status" label + TimelineList in its own white,
// bordered, rounded card, rather than sitting directly in
// StatusPanelContent's gradient background.
export const PaymentStatusSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[3]};
  padding: ${theme.space[4]};
  background: ${theme.colors.bg.primary};
  border: 1px solid ${theme.colors.border.alt};
  border-radius: ${theme.radii.lg2};
`

export const EmptyStatusCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.space[2]};
  padding: ${theme.space[6]} ${theme.space[3]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
`

export const TimelineList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`

// done: theme.colors.border.brand (thryv orange) and current:
// theme.colors.text.brandBlue (thryv dark blue) — matches the reference
// design's orange checkmarks / dark blue "in progress" dot, replacing the
// previous success-green/light info-blue pairing.
const timelineDotColor = ({state}) => {
  if (state === 'done') return theme.colors.border.brand
  if (state === 'current') return theme.colors.text.brandBlue
  return theme.colors.border.strong
}

export const TimelineItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${theme.space[3]};
  padding-bottom: ${theme.space[8]};
  position: relative;

  &:last-of-type {
    padding-bottom: 0;
  }

  // Solid orange once this step is done, dashed while the segment below is
  // still pending or in progress — border-left (not a solid background
  // fill) is what actually lets border-style: dashed render.
  &:not(:last-of-type)::before {
    content: '';
    position: absolute;
    left: 9px;
    top: 20px;
    bottom: 0;
    border-left: 2px ${({state}) => (state === 'done' ? 'solid' : 'dashed')}
      ${({state}) =>
        state === 'done'
          ? theme.colors.border.brand
          : theme.colors.border.default};
  }
`

export const TimelineDot = styled.span`
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: ${theme.radii.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.inverse};
  background: ${timelineDotColor};
  box-shadow: ${({state}) =>
    state === 'current' ? `0 0 0 4px ${palette.info[100]}` : 'none'};
`

export const TimelineBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 1px;
`

export const TimelineLabel = styled.span`
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.semibold};
  color: ${({state}) =>
    state === 'upcoming' ? theme.colors.text.muted : theme.colors.text.primary};
`

export const TimelineTimestamp = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
`

export const TimelineDescription = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`

export const ShareLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]};
`

export const ShareLinkInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${theme.space[2]} ${theme.space[3]};
  border: 1px solid ${theme.colors.border.default};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  background: ${theme.colors.bg.secondary};
`

// A slim rail that stays visible whether the drawer is open or closed so
// Payment Status can always be reopened. Rotated label reads top-to-bottom
// (writing-mode), matching a standard sideways drawer tab. align-self:
// flex-start opts out of PaymentStatusDock's default stretch, so the tab
// keeps its own compact height instead of stretching to the dock's full
// fixed height — its size never changes between collapsed/expanded, since
// toggling only mounts/unmounts the separate StatusPanel next to it.
// margin-top holds the tab at the same visual spot it occupied before
// PaymentStatusDock became full viewport height (see PaymentStatusDock's
// comment) — the same 64px header + 146px estimate used there previously.
// Same two blues as the solid-gradiant Button variant (see
// core/shared/Button/Button.styles.js), just right-to-left instead of that
// variant's radial gradient, so this still reads as the same "primary
// action" blue used for Continue/Send buttons elsewhere in the wizard.
// `expanded` (== !statusCollapsed, i.e. the drawer is open) is the "enabled"
// full-opacity look; collapsed is a faded/translucent look — still fully
// clickable (never disabled, no pointer-events change), just visually
// muted until it's actually opened.
export const CollapseTab = styled.button`
  align-self: flex-start;
  margin-top: calc(64px + 180px);
  // margin-top: calc(64px + 146px);
  flex: 0 0 36px;
  width: 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.space[3]};
  padding: ${theme.space[3]} 0;
  border-radius: ${theme.radii.lg2};
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  background: radial-gradient(
    129.31% 106.97% at 129.31% 50.31%,
    #5378fc 0%,
    #071125 100%
  );
  box-shadow: 0px 0px 12px 0px #5378fc99;
  border-top: 1.5px;
  border-bottom: : 1.5px;
  border-left: 1.5px;
  border-style: solid;
  border-color: #5378fc;
  color: ${theme.colors.text.inverse};
  opacity: ${({expanded}) => (expanded ? 1 : 0.6)};
  cursor: pointer;
  transition: ${theme.transitions.normal};

  &:hover {
    opacity: 1;
    filter: brightness(1.1);
  }
`

export const CollapseTabChevron = styled.span`
  font-size: ${theme.fontSizes.base};
  line-height: 1;
`

export const CollapseTabLabel = styled.span`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.semibold};
  letter-spacing: 0.06em;
  // text-transform: uppercase;
  white-space: nowrap;
  flex: 1;
  display: flex;
  align-items: center;
`
